import React, { useState, useEffect } from 'react';
import { TranslationBankItemsModel } from 'models/TranslationBankItemsModel';
import { ModalWrapper } from 'components/ModalWrapper';
import { ITranslationBank } from 'types/translationBank';
import styled from 'styled-components';

interface IProps {
  itemId: number;
  itemsModel: TranslationBankItemsModel;
  onClose: () => void;
  onSuccess: () => void;
}

const MoveItemModal: React.FC<IProps> = ({ itemId, itemsModel, onClose, onSuccess }) => {
  const [banks, setBanks] = useState<ITranslationBank[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<number>(0);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadBanks = async () => {
      const available = await itemsModel.getAvailableBanks();
      setBanks(available);
    };
    loadBanks();
  }, [itemsModel]);

  const handleMove = async () => {
    if (selectedBankId <= 0) {
      setError('Select a target bank');
      return;
    }

    setError('');
    setIsMoving(true);

    const result = await itemsModel.moveItem(itemId, selectedBankId);
    if (result.ok) {
      onSuccess();
    } else {
      setError('Failed to move item');
      setIsMoving(false);
    }
  };

  return (
    <ModalWrapper title="Move Item to Bank" width={400} height={300} onClose={onClose} disableBackdropClick>
      <Content>
        {banks.length === 0 ? (
          <EmptyMessage>No other banks with the same agent type available.</EmptyMessage>
        ) : (
          <>
            <Label>Select target bank:</Label>
            <Select value={selectedBankId} onChange={(e) => setSelectedBankId(Number(e.target.value))} disabled={isMoving}>
              <option value={0}>-- Select bank --</option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name} ({bank.itemCount} items)
                </option>
              ))}
            </Select>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose} disabled={isMoving}>
          Cancel
        </CancelButton>
        {banks.length > 0 && (
          <MoveButton onClick={handleMove} disabled={isMoving || selectedBankId <= 0}>
            {isMoving ? 'Moving...' : 'Move'}
          </MoveButton>
        )}
      </Footer>
    </ModalWrapper>
  );
};

export default MoveItemModal;

const Content = styled.div`
  padding: 16px;
  flex: 1;
`;

const Label = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const EmptyMessage = styled.div`
  color: #666;
  font-style: italic;
  padding: 20px 0;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 13px;
  margin-top: 10px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MoveButton = styled.button`
  padding: 8px 16px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
